import { useQueryClient } from "@tanstack/react-query";
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router";

import { githubCallback, queryKeys } from "@/lib/api";
import { logger } from "@/lib/logger";

export function GitHubCallbackPage() {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const [error, setError] = useState("");

  useEffect(() => {
    const code = searchParams.get("code");
    const state = searchParams.get("state");

    let cancelled = false;

    if (!code || !state) {
      // Defer the synchronous setState off the effect's body so the
      // react-hooks lint stays happy.
      Promise.resolve().then(() => {
        if (!cancelled) {
          setError("Missing code or state from GitHub. Please try connecting again.");
        }
      });
      return () => {
        cancelled = true;
      };
    }

    githubCallback({ body: { code, state } })
      .then(() => {
        if (!cancelled) {
          // Refresh the access-related caches ourselves so correctness doesn't
          // depend on the OAuth redirect triggering a full-page reload.
          void queryClient.invalidateQueries({ queryKey: queryKeys.githubStatus });
          void queryClient.invalidateQueries({ queryKey: queryKeys.accessStatus });
          navigate("/home", { replace: true });
        }
      })
      .catch((err) => {
        if (cancelled) {
          return;
        }
        logger.error("[github-callback] failed:", err);
        const body = (err as { body?: { detail?: string } })?.body;
        setError(body?.detail ?? "Failed to connect GitHub account. Please try again.");
      });

    return () => {
      cancelled = true;
    };
  }, [searchParams, navigate, queryClient]);

  if (error) {
    return (
      <div className="mx-auto max-w-md px-6 py-12 text-center">
        <h2 className="text-primary text-2xl font-normal">Connection Failed</h2>
        <p className="text-destructive mt-4 text-sm">{error}</p>
        <button
          onClick={() => navigate("/home", { replace: true })}
          className="text-primary mt-6 text-sm underline"
        >
          Back to Home
        </button>
      </div>
    );
  }

  return (
    <div className="mx-auto max-w-md px-6 py-12 text-center">
      <h2 className="text-primary text-2xl font-normal">Connecting GitHub...</h2>
      <p className="text-muted mt-4 text-sm">Please wait while we complete the connection.</p>
    </div>
  );
}
